import React from 'react';
import { Phone, Mail, MapPin, Clock, ArrowUp } from 'lucide-react';
import Logo from './Logo';

const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    'Agencement intérieur',
    'Menuiseries extérieures',
    'Terrasses & pergolas',
    'Cuisines sur mesure',
    'Dressings & placards',
    'Isolation & plâtrerie'
  ];

  const zones = [
    'Vannes',
    'Golfe du Morbihan',
    'Auray - Carnac - Quiberon',
    'Lorient',
    'Pontivy',
    'Ploërmel'
  ];

  return (
    <footer className="bg-stone-900 text-white">
      {/* Contenu principal */}
      <div className="container-max section-padding py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Informations entreprise */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <Logo size="medium" showText={true} variant="white" />
            </div>
            
            <p className="text-stone-300 mb-6 leading-relaxed">
              Jérémy ARS, artisan menuisier passionné, réalise vos projets d'agencement 
              intérieur et extérieur dans le Morbihan avec savoir-faire et créativité.
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-stone-300">
                <Phone className="w-4 h-4 text-wood-400" />
                <a href="tel:+33676825228" className="hover:text-white">
                  +33 6 76 82 52 28
                </a>
              </div>
              <div className="flex items-center space-x-3 text-stone-300">
                <Mail className="w-4 h-4 text-wood-400" />
                <a href="mailto:contact@ars-agencement.bzh" className="hover:text-white">
                  contact@ars-agencement.bzh
                </a>
              </div>
              <div className="flex items-start space-x-3 text-stone-300">
                <MapPin className="w-4 h-4 text-wood-400 mt-1" />
                <div>
                  <div>2 Rue Joseph le Brix</div>
                  <div>56400 Plougoumelen</div>
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6">Nos prestations</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a 
                    href="/#prestations" 
                    className="text-stone-300 hover:text-wood-400 transition-colors"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Zone d'intervention */}
          <div>
            <h4 className="text-lg font-bold mb-6">Zone d'intervention</h4>
            <ul className="space-y-3">
              {zones.map((zone, index) => (
                <li key={index} className="text-stone-300">
                  {zone}
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 bg-stone-800 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-4 h-4 text-wood-400" />
                <span className="font-medium">Horaires</span>
              </div>
              <div className="text-sm text-stone-300">
                <div>Lun-Ven: 8h-18h</div>
                <div>Samedi: 9h-12h</div>
                <div className="text-wood-400">Sur rendez-vous</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-lg font-bold mb-6">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="/" 
                  className="text-stone-300 hover:text-wood-400 transition-colors"
                >
                  Accueil
                </a>
              </li>
              <li>
                <a 
                  href="/#apropos" 
                  className="text-stone-300 hover:text-wood-400 transition-colors"
                >
                  À propos
                </a>
              </li>
              <li>
                <a 
                  href="/#prestations" 
                  className="text-stone-300 hover:text-wood-400 transition-colors"
                >
                  Prestations
                </a>
              </li>
              <li>
                <a 
                  href="/#realisations" 
                  className="text-stone-300 hover:text-wood-400 transition-colors"
                >
                  Réalisations
                </a>
              </li>
              <li>
                <a 
                  href="/#contact" 
                  className="text-stone-300 hover:text-wood-400 transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>

            {/* Call to action */}
            <div className="mt-8">
              <a 
                href="/#contact"
                className="inline-block bg-wood-600 hover:bg-wood-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                Demander un devis
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Barre du bas */}
      <div className="border-t border-stone-800">
        <div className="container-max section-padding py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-stone-400">
              <div>© 2026 ARS Agencement - Tous droits réservés</div>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="/mentions-legales" className="hover:text-stone-300">Mentions légales</a>
                <a href="/politique-confidentialite" className="hover:text-stone-300">Politique de confidentialité</a>
                <a href="/cgv" className="hover:text-stone-300">CGV</a>
              </div>
            </div>

            <div className="text-xs text-stone-500 text-center md:text-left mt-2 md:mt-0">
              Création : <a href="https://izenah.bzh" target="_blank" rel="noopener noreferrer" className="text-wood-500 hover:text-wood-400">izenah.bzh</a>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-sm text-stone-400">
                Artisan certifié - Morbihan (56)
              </div>
              <button
                onClick={scrollToTop}
                className="bg-wood-600 hover:bg-wood-700 p-2 rounded-full transition-colors duration-200"
                aria-label="Retour en haut"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
